import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "src/components/app/Button";
import Header from "src/components/app/layout/Header";
import Input from "src/components/app/Input";
import Layout from "src/components/app/layout/Layout";
import Radio from "src/components/common/Radio";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import {
  getPatientDetailsRequest,
  updateProfileRequest,
} from "src/redux/slices/authSlice";
import { EMAIL_REGEX, NUMERICS } from "src/constants/regex";
import { toast } from "sonner";
import moment from "moment";
import analytics from "src/helpers/analytics";
import { SCREEN_NAMES } from "src/helpers/analytics/events";
import { sendMessageToParent } from "src/helpers/common";
import { genderOptions, POST_MESSAGE_EVENTS } from "src/constants/common";

const EditProfileScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { patient, updateProfile } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    weight: "",
    height_ft: "",
    height_inches: "",
    dob: "",
    email: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    gender: "",
    weight: "",
    height_ft: "",
    height_inches: "",
    dob: "",
    email: "",
  });

  useEffect(() => {
    // page view
    analytics.pageView(SCREEN_NAMES.EDIT_PROFILE_SCREEN);
  }, []);

  // Pre-populate form with patient data
  useEffect(() => {
    if (patient?.data) {
      // Extract height from decimal format (e.g., 5.03 = 5 feet 3 inches)
      let heightFt = "";
      let heightInches = "";

      if (patient.data.height && patient.data.height_unit === "feet_inch") {
        const heightNum =
          typeof patient.data.height === "string"
            ? parseFloat(patient.data.height)
            : patient.data.height;
        heightFt = Math.floor(heightNum).toString();
        heightInches = Math.round(
          (heightNum - Math.floor(heightNum)) * 100
        ).toString();
      }

      // Extract weight
      const weightValue = patient.data.weight
        ? (typeof patient.data.weight === "string"
            ? parseFloat(patient.data.weight)
            : patient.data.weight
          ).toString()
        : "";

      setFormData({
        name: patient.data.name || "",
        gender: patient.data.gender || "",
        weight: weightValue,
        height_ft: heightFt,
        height_inches: heightInches,
        dob: patient.data.dob
          ? moment(patient.data.dob).format("YYYY-MM-DD")
          : "",
        email: patient.data.email || "",
      });
    }
  }, [patient]);

  const validateField = (fieldName: string, value: string) => {
    let error = "";

    switch (fieldName) {
      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (!EMAIL_REGEX.test(value)) {
          error = "Please enter a valid email address";
        } else if (value.length < 3) {
          error = "Email is too short";
        }
        break;

      case "gender":
        if (!value) {
          error = "Gender is required";
        }
        break;

      case "weight":
        if (!value.trim()) {
          error = "Weight is required";
        } else if (!NUMERICS.test(value)) {
          error = "Weight must be a number";
        } else if (Number(value) < 1 || Number(value) > 500) {
          error = "Weight must be between 1 and 500 kg";
        }
        break;

      case "height_ft":
        if (!value.trim()) {
          error = "Height (ft) is required";
        } else if (!NUMERICS.test(value)) {
          error = "Height must be a number";
        } else if (Number(value) < 1 || Number(value) > 8) {
          error = "Height must be between 1 and 8 feet";
        }
        break;

      case "height_inches":
        if (!value.trim()) {
          error = "Height (inches) is required";
        } else if (!NUMERICS.test(value)) {
          error = "Inches must be a number";
        } else if (Number(value) < 0 || Number(value) > 11) {
          error = "Inches must be between 0 and 11";
        }
        break;

      case "dob":
        if (!value) {
          error = "Date of birth is required";
        } else {
          const dobDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - dobDate.getFullYear();
          if (age < 0 || age > 150) {
            error = "Please enter a valid date of birth";
          }
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value: fieldValue } = e.target;

    setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));

    const error = validateField(fieldName, fieldValue);
    setErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const handleUpdateProfile = () => {
    // Validate all required fields
    const newErrors = { ...errors };
    let hasErrors = false;

    const requiredFields = [
      "gender",
      "weight",
      "height_ft",
      "height_inches",
      "dob",
      "email",
    ];

    requiredFields.forEach((key) => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) {
        newErrors[key as keyof typeof errors] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      toast.error("Please fill all required fields correctly");
      return;
    }

    // Calculate height in feet.inches format (e.g., 4.11 for 4 feet 11 inches)
    const heightInFeet =
      Number(formData.height_ft) + Number(formData.height_inches) / 100;

    const payload = {
      name: formData.name,
      gender: formData.gender,
      updateHeightWeight: 1,
      height: Number(heightInFeet.toFixed(2)),
      height_unit: "feet_inch",
      weight: Number(formData.weight),
      weight_unit: "kg",
      dob: formData.dob,
      email: formData.email,
    };

    new Promise((resolve, reject) => {
      dispatch(
        updateProfileRequest({
          payload: {
            payload,
            header: {
              userFrom: "PSP",
              source: "GoodFlipCare",
            },
          },
          resolve,
          reject,
        })
      );
    })
      .then((res: any) => {
        if (Number(res?.code || 0) === 1) {
          toast.success("Profile updated successfully");

          // Update to App to fetch updated patient details
          sendMessageToParent(POST_MESSAGE_EVENTS.USER_DETAILS_UPDATED);

          // Get Updated Patient Details
          //navigate(-1);
          new Promise((resolve, reject) => {
            dispatch(
              getPatientDetailsRequest({
                resolve,
                reject,
              })
            );
          })
            .then((res: any) => {
              if (Number(res?.code || 0) === 1) {
                const supportProgramKey =
                  res?.data?.patient_meta_info?.support_program_key;
                navigate(`/app/${supportProgramKey}/profile-screen`, {
                  replace: true,
                });
              }
            })
            .catch((err: any) => {
              console.error("err", err);
            });
        } else {
          toast.error(res?.msg || "Failed to update profile");
        }
      })
      .catch((error) => {
        console.error("Error updating profile:", error);
        toast.error("Failed to update profile");
      });
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      header={
        <Header
          title="Edit Profile"
          titlePosition="center"
          showBackButton={true}
          onBackClick={() => {
            navigate(-1);
          }}
        />
      }
      stickyHeader={true}
      footer={
        <div className="bg-white p-4 border-t border-gray-200">
          <Button
            rounded="full"
            fullWidth={true}
            onClick={handleUpdateProfile}
            loading={updateProfile?.isLoading}
            disabled={updateProfile?.isLoading}
          >
            Save Details
          </Button>
        </div>
      }
      stickyFooter={true}
    >
      <div className="relative w-full px-4 py-4">
        <div className="flex flex-col gap-y-3">
          <Input
            label="Patient Name"
            name="name"
            type="text"
            placeholder="Enter patient name"
            fullWidth={true}
            value={formData.name}
            onChange={handleChange}
            disabled={true}
          />
          <div className="relative w-full">
            <label
              htmlFor="gender"
              className="block text-sm font-medium text-[var(--text-secondary)] mb-2"
            >
              Gender
            </label>
            <div className="flex items-center gap-4">
              {genderOptions.map((gender, index) => (
                <div
                  className="flex items-center gap-2"
                  key={index}
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, gender: gender.value }));
                  }}
                >
                  <Radio checked={formData.gender === gender.value} />
                  <span className="font-medium text-sm text-gray-500">
                    {gender.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Input
            label="Weight (kg)"
            name="weight"
            type="number"
            placeholder="Enter weight"
            fullWidth={true}
            value={formData.weight}
            onChange={handleChange}
            error={!!errors.weight}
            helperText={errors.weight}
          />
          <div className="flex items-start gap-4">
            <Input
              label="Height (ft)"
              name="height_ft"
              type="number"
              placeholder="Enter height(ft)"
              fullWidth={true}
              value={formData.height_ft}
              onChange={handleChange}
              error={!!errors.height_ft}
              helperText={errors.height_ft}
            />
            <Input
              label="Height (inches)"
              name="height_inches"
              type="number"
              placeholder="Enter height(inches)"
              fullWidth={true}
              value={formData.height_inches}
              onChange={handleChange}
              error={!!errors.height_inches}
              helperText={errors.height_inches}
            />
          </div>
          <Input
            label="Date of Birth"
            name="dob"
            type="date"
            placeholder="Enter date of birth"
            fullWidth={true}
            value={formData.dob}
            onChange={handleChange}
            error={!!errors.dob}
            helperText={errors.dob}
          />
          <Input
            label="Email"
            name="email"
            type="email"
            placeholder="Enter email"
            fullWidth={true}
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
        </div>
      </div>
    </Layout>
  );
};

export default EditProfileScreen;
